import models.BankoGameEngine.banksLocked
import models.DomainGame
import models.GameSettings
import models.Player
import models.Setting

// Game-screen decisions shared by every UI (Compose and the native SwiftUI app): who may act, who
// ¡Banko! applies to, which lobby settings show. Pure and Compose-free so both front ends agree
// and the rules are unit tested once.

data class GameScreenState(
    val selfPlayer: Player?,
    val hostPlayer: Player?,
    val currentTurnsPlayer: Player?,
    val isHost: Boolean,
    val isStarted: Boolean,
    val isGameOver: Boolean,
    val selfIsCurrentTurnsPlayer: Boolean,
    val hostCanBankForOthers: Boolean,
    val currentTurnsPlayerIsHostCreated: Boolean
) {
    /**
     * Whether the local user may roll/bank for the current turn's player. Never true on a bot's
     * turn — bots drive themselves, so no human (not even the host) acts for them.
     */
    val canActOnCurrentTurn: Boolean
        get() = !isGameOver &&
                currentTurnsPlayer?.isBot != true &&
                (selfIsCurrentTurnsPlayer ||
                        (isHost && hostCanBankForOthers) ||
                        (currentTurnsPlayerIsHostCreated && isHost))

    companion object {
        fun from(game: DomainGame?, playerName: String): GameScreenState {
            val selfPlayer = game?.players?.firstOrNull { it.name == playerName }
            val hostPlayer = game?.host?.let { host ->
                game.players.firstOrNull { it.name == host }
            }
            val currentTurnsPlayer = game?.currentPlayer?.let { currentPlayer ->
                game.players.firstOrNull { it.name == currentPlayer }
            }
            val isHost = hostPlayer?.name == playerName
            val isStarted = game?.round != null
            val isGameOver = isGameOver(game)
            val selfIsCurrentTurnsPlayer = selfPlayer == currentTurnsPlayer
            val hostCanBankForOthers = game?.settings?.let {
                GameSettings.isEnabled(it, GameSettings.HostControl)
            } ?: GameSettings.HostControl.defaultValue
            val currentTurnsPlayerIsHostCreated = currentTurnsPlayer?.hostCreated ?: false

            return GameScreenState(
                selfPlayer = selfPlayer,
                hostPlayer = hostPlayer,
                currentTurnsPlayer = currentTurnsPlayer,
                isHost = isHost,
                isStarted = isStarted,
                isGameOver = isGameOver,
                selfIsCurrentTurnsPlayer = selfIsCurrentTurnsPlayer,
                hostCanBankForOthers = hostCanBankForOthers,
                currentTurnsPlayerIsHostCreated = currentTurnsPlayerIsHostCreated
            )
        }
    }
}

/** A game is over either when the host has closed it or when play passes its final round. */
internal fun isGameOver(game: DomainGame?): Boolean =
    game?.finished == true || game?.round?.let { it.roundNum > game.endRoundNum } == true

/** Guests can bank their own active player only while that player owns the current turn. */
internal fun canPlayerBankForSelf(game: DomainGame, playerName: String): Boolean {
    val round = game.round ?: return false
    return game.currentPlayer == playerName && playerName in round.activeOrderedPlayerNames
}

internal fun isPlayerActive(game: DomainGame?, playerName: String): Boolean {
    return game?.round?.activeOrderedPlayerNames?.contains(playerName) ?: false
}

/**
 * The active players the host can ¡Banko! for this turn — everyone still in the round when host
 * control is enabled, otherwise just the host-created (device-less) players. Shared by the ¡Banko!
 * button and the player picker so they always agree on the available options.
 */
internal fun bankablePlayers(game: DomainGame?): List<Player> {
    val round = game?.round ?: return emptyList()
    // Bots play themselves (the bot AI rolls/banks on its own turn), so the host never banks for
    // them — only for device-less human players the host manages.
    val activePlayers = game.players.filter { it.name in round.activeOrderedPlayerNames && !it.isBot }
    val hostCanControlOtherDevices = GameSettings.isEnabled(game.settings, GameSettings.HostControl)
    return if (hostCanControlOtherDevices) activePlayers else activePlayers.filter { it.hostCreated }
}

/**
 * What the ¡Banko! button does right now: whether it's enabled, whether it opens the player picker
 * (the host has a real choice of who to bank for), and otherwise whose points it banks.
 */
internal data class BankoActionPlan(
    val enabled: Boolean,
    val opensPicker: Boolean,
    val directTarget: String,
)

internal fun bankoActionPlan(
    game: DomainGame,
    gameState: GameScreenState,
    playerName: String,
): BankoActionPlan {
    val round = game.round
    val selectablePlayers = bankablePlayers(game)
    val hostBanksForOthers = gameState.isHost &&
            (game.players.any { it.hostCreated } || gameState.hostCanBankForOthers)
    // Only open the picker when the host actually has a choice. With a single bankable
    // player (usually just themselves) there's nothing to pick, so ¡Banko! immediately.
    val opensPicker = hostBanksForOthers && selectablePlayers.size > 1
    val bankingLocked = round?.banksLocked() == true
    val hostControlledActivePlayers = if (round == null) {
        emptyList()
    } else {
        game.players.filter { it.hostCreated && !it.isBot }.map { it.name }
            .intersect(round.activeOrderedPlayerNames.toSet()).toList()
    }
    val canBankAsHost = isPlayerActive(game, playerName) ||
            (gameState.hostCanBankForOthers && gameState.isHost) ||
            (gameState.isHost && hostControlledActivePlayers.isNotEmpty())
    val canBankAsSelf = !gameState.isHost && canPlayerBankForSelf(game, playerName)
    val enabled = !bankingLocked && if (gameState.isHost) canBankAsHost else canBankAsSelf
    val directTarget = if (hostBanksForOthers) {
        // One bankable player — bank them directly (themselves if active).
        selectablePlayers.firstOrNull()?.name ?: playerName
    } else {
        playerName
    }
    return BankoActionPlan(enabled = enabled, opensPicker = opensPicker, directTarget = directTarget)
}

/** The real-dice entry grid shows when manual entry is on and this device may act on the turn. */
internal fun manualDiceGridVisible(game: DomainGame?, gameState: GameScreenState): Boolean {
    val manualEntryIsAllowed = game?.settings?.let {
        GameSettings.isEnabled(it, GameSettings.ManualEntry)
    } ?: GameSettings.ManualEntry.defaultValue
    return manualEntryIsAllowed && gameState.canActOnCurrentTurn && gameState.isStarted
}

/**
 * Who takes over hosting if [playerName] leaves: the next player in order on their own device
 * (host-created players and bots are driven by the host's device, so they can't host).
 */
internal fun leaveSuccessorName(game: DomainGame?, playerName: String): String? =
    game?.orderedPlayerNames
        ?.filter { it != playerName }
        ?.mapNotNull { name -> game.players.firstOrNull { it.name == name } }
        ?.firstOrNull { !it.hostCreated && !it.isBot }
        ?.name

/**
 * The lobby's on/off settings in display order. Settings that only matter across several devices
 * (host plays for others, nudging) are hidden on single-device games where they'd be no-ops.
 */
internal fun visibleLobbySettings(settings: List<Setting>, isNetworkGame: Boolean): List<Setting> =
    settings
        .filter {
            (it.value == "true" || it.value == "false") &&
                    (isNetworkGame || !GameSettings.isNetworkOnlySetting(it))
        }
        .sortedBy { GameSettings.displayOrder(it) }

internal fun isGameLengthPremiumUnlocked(
    isHost: Boolean,
    hostPerksActive: Boolean,
    bankoPlusActive: Boolean,
): Boolean = hostPerksActive || (isHost && bankoPlusActive)
